import { useRef } from 'react';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
import { useEmailSubmit } from '../../hooks/useEmailSubmit';

const GeneralInquiryForm = () => {
  const contactFormRef = useRef();
  const { isSubmitting, submit } = useEmailSubmit({
    successMessage: 'Message sent successfully! We will get back to you soon.',
    errorMessage: 'Failed to send message. Please try again or contact us directly.',
    sheetName: 'General Inquiries',
  });

  const buildParams = (fd) => ({
    emailParams: {
      name: fd.get('user_name'),
      email: fd.get('user_email'),
      phone: '-',
      message: `Request Type: General Inquiry\n\n${fd.get('message')}`,
    },
    sheetRow: {
      Name: fd.get('user_name'),
      Email: fd.get('user_email'),
      Message: fd.get('message'),
    },
  });

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <h2
        className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2A57] mb-6"
        style={{ fontFamily: 'Cabinet Grotesk' }}
        data-testid="contact-form-title"
      >
        Send Us a Message
      </h2>
      <p className="text-base text-[#6B7280] leading-relaxed mb-8">
        Have a general inquiry? Fill out the form below and our team will get back to
        you within 24 hours.
      </p>

      <form
        ref={contactFormRef}
        onSubmit={(e) => submit(e, contactFormRef, buildParams)}
        className="space-y-6"
        data-testid="contact-form"
      >
        <div>
          <label htmlFor="user_name" className="block text-sm font-medium text-[#0B2A57] mb-2">
            Your Name *
          </label>
          <input
            type="text"
            id="user_name"
            name="user_name"
            required
            disabled={isSubmitting}
            className="w-full px-4 py-3 rounded-lg border border-[#0B2A57]/20 focus:border-[#1E3D66] focus:ring-2 focus:ring-[#1E3D66]/20 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="John Doe"
            data-testid="contact-name-input"
          />
        </div>

        <div>
          <label htmlFor="user_email" className="block text-sm font-medium text-[#0B2A57] mb-2">
            Your Email *
          </label>
          <input
            type="email"
            id="user_email"
            name="user_email"
            required
            disabled={isSubmitting}
            className="w-full px-4 py-3 rounded-lg border border-[#0B2A57]/20 focus:border-[#1E3D66] focus:ring-2 focus:ring-[#1E3D66]/20 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="john@example.com"
            data-testid="contact-email-input"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-[#0B2A57] mb-2">
            Your Message *
          </label>
          <textarea
            id="message"
            name="message"
            required
            disabled={isSubmitting}
            rows={6}
            className="w-full px-4 py-3 rounded-lg border border-[#0B2A57]/20 focus:border-[#1E3D66] focus:ring-2 focus:ring-[#1E3D66]/20 outline-none transition-all resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="Tell us about your inquiry..."
            data-testid="contact-message-input"
          />
        </div>

        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
          whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
          className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#0B2A57] text-white font-medium transition-all hover:shadow-lg hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          data-testid="contact-submit-button"
        >
          {isSubmitting ? (
            'Sending...'
          ) : (
            <>
              Send Message
              <Send size={20} />
            </>
          )}
        </motion.button>
      </form>
    </motion.div>
  );
};

export default GeneralInquiryForm;
